import type { EmailTemplateListParams, IEmailTemplateRepository } from '@yawi/domain';
import type { PaginatedResponse } from '@yawi/domain';
import type {
  EmailTemplate,
  CreateEmailTemplateDto,
  UpdateEmailTemplateDto,
  EmailTemplatePreview,
  PreviewEmailTemplateDto,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

const BASE_URL = 'v1/admin/email-templates';

export class ApiEmailTemplateRepository implements IEmailTemplateRepository {
  async findPaginated(
    params?: EmailTemplateListParams
  ): Promise<PaginatedResponse<EmailTemplate>> {
    const client = getApiClient();
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    if (params?.code) query.set('code', params.code);
    if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
    if (params?.isSystem !== undefined) query.set('isSystem', String(params.isSystem));

    const queryString = query.toString();
    const response = await client.get<PaginatedResponse<EmailTemplate>>(
      queryString ? `${BASE_URL}?${queryString}` : BASE_URL
    );
    return response;
  }

  async findById(id: string): Promise<EmailTemplate> {
    const client = getApiClient();
    const response = await client.get<EmailTemplate>(`${BASE_URL}/${id}`);
    return response;
  }

  async findByTenant(tenantId: string): Promise<EmailTemplate[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<EmailTemplate>>(`${BASE_URL}?tenantId=${tenantId}`);
    return response.data;
  }

  async create(data: CreateEmailTemplateDto): Promise<EmailTemplate> {
    const client = getApiClient();
    const response = await client.post<EmailTemplate>(BASE_URL, data);
    return response;
  }

  async update(id: string, data: UpdateEmailTemplateDto): Promise<EmailTemplate> {
    const client = getApiClient();
    const response = await client.patch<EmailTemplate>(`${BASE_URL}/${id}`, data);
    return response;
  }

  async delete(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${BASE_URL}/${id}`);
  }

  async preview(data: PreviewEmailTemplateDto): Promise<EmailTemplatePreview> {
    const client = getApiClient();
    const response = await client.post<EmailTemplatePreview>(`${BASE_URL}/preview`, data);
    return response;
  }

  async duplicate(id: string): Promise<EmailTemplate> {
    const client = getApiClient();
    const response = await client.post<EmailTemplate>(`${BASE_URL}/${id}/duplicate`);
    return response;
  }

  async setDefault(id: string): Promise<EmailTemplate> {
    const client = getApiClient();
    const response = await client.post<EmailTemplate>(`${BASE_URL}/${id}/set-default`);
    return response;
  }
}
